import { Globe, Loader2, Lock, Sparkles, UserPlus, X } from 'lucide-react';
import * as React from 'react';
import { useAgentsHost } from './host.tsx';
import { AgentIcon } from './AgentIcon.tsx';
import { AGENT_KEY_RE, keyFromTitle, type Agent, type Person } from './model.ts';
import { btnGhost, btnPrimary, iconBtn, input, messageOf, Modal, unwrap } from './ui.tsx';

const A = '/agents' as const;

/** "Build an agent": a name, a display name, an icon and a description; the rest on the agent page. */
export function CreateAgentDialog({
  taken,
  onClose,
  onCreated,
}: {
  taken: ReadonlySet<string>;
  onClose: () => void;
  onCreated: (a: Agent) => void;
}) {
  const api = useAgentsHost().client.fetch;
  const [title, setTitle] = React.useState('');
  const [key, setKey] = React.useState('');
  const [keyTouched, setKeyTouched] = React.useState(false);
  const [icon, setIcon] = React.useState('🤖');
  const [description, setDescription] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const name = keyTouched ? key : keyFromTitle(title);
  const problem = !title.trim()
    ? 'Give the agent a display name.'
    : !AGENT_KEY_RE.test(name)
      ? 'The name starts with a letter: lower case letters, digits, _ and - (at most 58).'
      : taken.has(name)
        ? `An agent '${name}' already exists here.`
        : '';

  const submit = async () => {
    if (problem) return;
    setBusy(true);
    setErr('');
    try {
      const a = await unwrap(
        api.POST(A, {
          body: {
            key: name,
            title: title.trim(),
            ...(icon.trim() ? { icon: icon.trim() } : {}),
            ...(description.trim() ? { description: description.trim() } : {}),
          },
        }),
      );
      onCreated(a);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={
        <>
          <Sparkles size={18} className="text-nav-active" /> Build an agent
        </>
      }
      onClose={onClose}
      testId="agent-create-dialog"
      footer={
        <>
          <button type="button" className={btnGhost} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={btnPrimary}
            data-testid="agent-create-submit"
            disabled={busy || !!problem}
            onClick={() => void submit()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Create agent
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-page-muted">
          An agent answers over this project through its tools: operations of the platform or of the project’s
          apps. Name it here; its persona, tools and safeguards are set on its page.
        </p>
        <div className="flex gap-2">
          <label className="block w-20 shrink-0">
            <span className="text-xs text-page-muted">Icon</span>
            <div className="flex items-center gap-1">
              <input
                className={input}
                data-testid="agent-create-icon"
                value={icon}
                maxLength={16}
                onChange={(e) => setIcon(e.target.value)}
              />
            </div>
          </label>
          <label className="block flex-1">
            <span className="text-xs text-page-muted">Display name</span>
            <input
              className={input}
              data-testid="agent-create-title"
              value={title}
              autoFocus
              maxLength={120}
              placeholder="Order desk"
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-page-muted">Name (what chats, tests and the ledger call it)</span>
          <input
            className={input + ' font-mono'}
            data-testid="agent-create-key"
            value={name}
            maxLength={58}
            onChange={(e) => {
              setKeyTouched(true);
              setKey(e.target.value);
            }}
          />
        </label>
        <label className="block">
          <span className="text-xs text-page-muted">Description</span>
          <textarea
            className={input + ' min-h-[64px]'}
            data-testid="agent-create-description"
            value={description}
            maxLength={4000}
            placeholder="What it answers, for whom"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <div className="flex items-center gap-2 text-sm text-page-muted">
          <AgentIcon icon={icon.trim() || null} size={16} /> {title.trim() || 'Your agent'}
        </div>
        {problem && title ? (
          <div className="text-xs text-amber-700 dark:text-amber-400">{problem}</div>
        ) : null}
        {err ? (
          <div className="text-sm text-red-600" data-testid="agent-create-error">
            {err}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Who can use the agent: the organization, or privately its owner and the people added by name (the people only
 * matter for a private agent, so they show for one, or when some were added).
 */
export function ShareDialog({
  agent,
  onClose,
  onChanged,
}: {
  agent: Agent;
  onClose: () => void;
  onChanged: (a: Agent) => void;
}) {
  const api = useAgentsHost().client.fetch;
  const [people, setPeople] = React.useState<Person[]>([]);
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [err, setErr] = React.useState('');
  const path = { key: agent.key };

  React.useEffect(() => {
    let alive = true;
    unwrap(api.GET('/agent-people'))
      .then((r) => alive && setPeople(r.people))
      .catch(() => alive && setPeople([]));
    return () => {
      alive = false;
    };
  }, [api]);

  const refresh = async () => onChanged(await unwrap(api.GET(`${A}/{key}`, { params: { path } })));

  const setVisibility = async (visibility: 'private' | 'organization') => {
    if (visibility === agent.visibility) return;
    setBusy('visibility');
    setErr('');
    try {
      onChanged(await unwrap(api.PUT(`${A}/{key}`, { params: { path }, body: { visibility } })));
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy('');
    }
  };

  const add = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy('add');
    setErr('');
    try {
      onChanged(await unwrap(api.POST(`${A}/{key}/shares`, { params: { path }, body: { email: e } })));
      setEmail('');
    } catch (x) {
      setErr(messageOf(x));
    } finally {
      setBusy('');
    }
  };

  const remove = async (userId: string) => {
    setBusy(`rm:${userId}`);
    setErr('');
    try {
      await unwrap(api.DELETE(`${A}/{key}/shares/{userId}`, { params: { path: { ...path, userId } } }));
      await refresh();
    } catch (x) {
      setErr(messageOf(x));
    } finally {
      setBusy('');
    }
  };

  const shared = new Set(agent.shares.map((s) => s.id));
  const options = people.filter((p) => !shared.has(p.id) && p.id !== agent.owner?.id);
  const Choice = ({
    value,
    icon: Icon,
    label,
    help,
  }: {
    value: 'private' | 'organization';
    icon: typeof Lock;
    label: string;
    help: string;
  }) => (
    <label
      className={
        'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ' +
        (agent.visibility === value ? 'border-nav-active bg-nav-active/5' : 'border-card-border')
      }
    >
      <input
        type="radio"
        name="agent-visibility"
        data-testid={`agent-share-visibility-${value}`}
        checked={agent.visibility === value}
        disabled={!!busy}
        onChange={() => void setVisibility(value)}
        className="mt-1"
      />
      <Icon size={16} className="mt-0.5 text-page-muted" />
      <span>
        <span className="block text-sm text-page-text">{label}</span>
        <span className="block text-xs text-page-muted">{help}</span>
      </span>
    </label>
  );

  return (
    <Modal
      title={
        <span className="inline-flex items-center gap-2">
          <AgentIcon icon={agent.icon} />
          <span>
            Who can use it
            <span className="block text-xs font-normal text-page-muted">{agent.title}</span>
          </span>
        </span>
      }
      onClose={onClose}
      testId="agent-share-dialog"
      footer={
        <button type="button" className={btnGhost} data-testid="agent-share-close" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="space-y-3">
        <div className="space-y-2">
          <Choice
            value="organization"
            icon={Globe}
            label="Organization"
            help="Everyone who can open this project"
          />
          <Choice
            value="private"
            icon={Lock}
            label="Private"
            help={
              agent.owner
                ? 'Only its owner and the people added below'
                : 'Only the project’s managers and the people added below'
            }
          />
        </div>
        {agent.visibility === 'private' || agent.shares.length ? (
          <div className="ml-6" data-testid="agent-share-people">
            <div className="flex gap-2">
              <input
                className={input}
                data-testid="agent-share-email-input"
                list="agent-share-people"
                placeholder="Add a person — name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void add();
                }}
              />
              <datalist id="agent-share-people">
                {options.map((p) => (
                  <option key={p.id} value={p.email}>
                    {p.name ?? p.email}
                  </option>
                ))}
              </datalist>
              <button
                type="button"
                className={btnGhost}
                data-testid="agent-share-add"
                disabled={!email.trim() || !!busy}
                onClick={() => void add()}
              >
                {busy === 'add' ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Add
              </button>
            </div>
            <ul className="mt-3 divide-y divide-divider">
              {agent.shares.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-2 py-2 text-sm"
                  data-testid={`agent-share-${s.email}`}
                >
                  <span className="flex-1 min-w-0 truncate">
                    {s.name ? `${s.name} · ` : ''}
                    <span className="text-page-muted">{s.email}</span>
                  </span>
                  <button
                    type="button"
                    className={iconBtn}
                    title="Remove"
                    data-testid={`agent-unshare-${s.id}`}
                    disabled={!!busy}
                    onClick={() => void remove(s.id)}
                  >
                    {busy === `rm:${s.id}` ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                  </button>
                </li>
              ))}
            </ul>
            {!agent.shares.length ? (
              <div className="text-sm text-page-muted" data-testid="agent-share-empty">
                Nobody added yet.
              </div>
            ) : null}
          </div>
        ) : null}
        {err ? (
          <div className="text-sm text-red-600" data-testid="agent-share-error">
            {err}
          </div>
        ) : null}
        <div className="text-xs text-page-muted" data-testid="agent-share-owner">
          {agent.owner
            ? `Owner: ${agent.owner.name ?? agent.owner.email}`
            : 'Managed by the project’s managers (no single owner).'}{' '}
          · <span className="text-green-700 dark:text-green-400">✓ Saved at once</span>
        </div>
      </div>
    </Modal>
  );
}
